import type {BancoDeFinanciador, Financiador} from '@cnc/contracts';
import {useState} from 'react';
import {useBancosDeFinanciador, useGuardarBanco, useGuardarFinanciador} from '../api/financiacion';
import {tr} from '../i18n/idioma';
import {AYUDA_TERMINOS_DE_FINANCIADOR as A} from './ayuda-financiacion-bluequest';
import {NotaDeAyuda} from './nota-de-ayuda';

const campo = 'mt-1 block w-full rounded-md border border-linea px-2 py-1 text-sm text-tinta';
// Porcentajes en pantalla (90 = 0,9).
const aPorcentaje = (v: number | null) => (v === null ? '' : String(Math.round(v * 1e8) / 1e6));
const dePorcentaje = (t: string) => (t.trim() === '' ? null : Number(t.replace(',', '.')) / 100);
const aNumero = (t: string) => (t.trim() === '' ? null : Number(t.replace(/\s/g, '').replace(',', '.')));

// Documentos que pide un financiador al financiar, sus condiciones para el TC
// calculation y sus bancos (Daniel, 15/10/2026).
export function DocumentosDelFinanciador({f, puedeEditar}: {f: Financiador; puedeEditar: boolean}) {
  const guardar = useGuardarFinanciador();
  const [abierto, setAbierto] = useState(f.documentos !== null);
  const [documentos, setDocumentos] = useState(f.documentos ?? '');
  const [porcentaje, setPorcentaje] = useState(aPorcentaje(f.porcentajeFinanciado));
  const [margen, setMargen] = useState(aPorcentaje(f.margen));
  const [cobro, setCobro] = useState(f.cobroDocumentosUsd === null ? '' : String(f.cobroDocumentosUsd));
  const [flete, setFlete] = useState(f.fletePorContenedorUsd === null ? '' : String(f.fletePorContenedorUsd));
  const [aviso, setAviso] = useState<string | null>(null);
  if (!f.financia) return null;
  const enviar = async () => {
    setAviso(null);
    try {
      await guardar.mutateAsync({
        codigo: f.codigo,
        cambios: {
          nombre: f.nombre,
          cupoUsd: f.cupoPropioUsd,
          notas: f.notas,
          documentos: documentos === 'bluequest' ? 'bluequest' : null,
          porcentajeFinanciado: dePorcentaje(porcentaje),
          margen: dePorcentaje(margen),
          cobroDocumentosUsd: aNumero(cobro),
          fletePorContenedorUsd: aNumero(flete),
        },
      });
      setAviso(tr('Guardado.', 'Saved.'));
    } catch (e) {
      setAviso(e instanceof Error ? e.message : String(e));
    }
  };
  if (!abierto)
    return (
      <button type="button" onClick={() => setAbierto(true)} className="-mt-2 ml-1 text-xs text-verde hover:underline">
        {tr(`+ Documentos y bancos de ${f.codigo}`, `+ ${f.codigo} documents and banks`)}
      </button>
    );
  return (
    <section
      className="-mt-2 space-y-3 rounded-b-lg border border-t-0 border-linea bg-papel/60 px-4 py-3"
      aria-label={tr(`Documentos y bancos de ${f.codigo}`, `${f.codigo} documents and banks`)}
    >
      <form
        className="grid gap-3 sm:grid-cols-[12rem_repeat(4,1fr)_auto] sm:items-end"
        onSubmit={e => {
          e.preventDefault();
          void enviar();
        }}
      >
        <label className="block text-xs text-apagado">
          {tr('Documentos al financiar', 'Documents when financing')}
          <NotaDeAyuda texto={A.documentos} dentroDeEtiqueta />
          <select
            value={documentos}
            disabled={!puedeEditar}
            onChange={e => setDocumentos(e.target.value)}
            className={campo}
          >
            <option value="">{tr('Ninguno', 'None')}</option>
            <option value="bluequest">Bluequest</option>
          </select>
        </label>
        <label className="block text-xs text-apagado">
          {tr('Financia (%)', 'Finances (%)')}
          <NotaDeAyuda texto={A.porcentaje} dentroDeEtiqueta />
          <input
            value={porcentaje}
            disabled={!puedeEditar}
            inputMode="decimal"
            onChange={e => setPorcentaje(e.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Margen (%)', 'Margin (%)')}
          <NotaDeAyuda texto={A.margen} dentroDeEtiqueta />
          <input
            value={margen}
            disabled={!puedeEditar}
            inputMode="decimal"
            onChange={e => setMargen(e.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Doc Collection (USD)', 'Doc Collection (USD)')}
          <NotaDeAyuda texto={A.cobroDocumentos} dentroDeEtiqueta />
          <input
            value={cobro}
            disabled={!puedeEditar}
            inputMode="decimal"
            onChange={e => setCobro(e.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Flete por contenedor (USD)', 'Freight per container (USD)')}
          <NotaDeAyuda texto={A.flete} dentroDeEtiqueta />
          <input
            value={flete}
            disabled={!puedeEditar}
            inputMode="decimal"
            onChange={e => setFlete(e.target.value)}
            className={campo}
          />
        </label>
        {puedeEditar && (
          <div className="flex flex-col items-start gap-1">
            <button
              type="submit"
              disabled={guardar.isPending}
              className="rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white disabled:opacity-50"
            >
              {tr('Guardar', 'Save')}
            </button>
            {aviso && <span className="text-xs">{aviso}</span>}
          </div>
        )}
      </form>
      <Bancos financiador={f.codigo} puedeEditar={puedeEditar} />
    </section>
  );
}

function Bancos({financiador, puedeEditar}: {financiador: string; puedeEditar: boolean}) {
  const bancos = useBancosDeFinanciador(financiador);
  const lista = bancos.data?.bancos ?? [];
  return (
    <div>
      <div className="text-xs font-medium text-apagado">{tr(`Bancos de ${financiador}`, `${financiador} banks`)}</div>
      {!lista.length && (
        <p className="text-xs text-apagado">
          {tr('Todavía no hay bancos. Agrega el primero abajo.', 'No banks yet. Add the first one below.')}
        </p>
      )}
      <div className="space-y-1">
        {lista.map(b => (
          <FilaDeBanco key={`${b.id}-${b.version}`} financiador={financiador} banco={b} puedeEditar={puedeEditar} />
        ))}
        {puedeEditar && (
          <FilaDeBanco key={`nuevo-${lista.length}`} financiador={financiador} banco={null} puedeEditar />
        )}
      </div>
    </div>
  );
}

function FilaDeBanco({
  financiador,
  banco,
  puedeEditar,
}: {
  financiador: string;
  banco: BancoDeFinanciador | null;
  puedeEditar: boolean;
}) {
  const guardar = useGuardarBanco();
  const [nombre, setNombre] = useState(banco?.nombre ?? '');
  const [enPl, setEnPl] = useState(banco?.nombreEnPackingList ?? '');
  const [comision, setComision] = useState(aPorcentaje(banco?.comisionInicial ?? null));
  const [cof, setCof] = useState(aPorcentaje(banco?.costoDeFondos ?? null));
  const [cupo, setCupo] = useState(
    banco?.cupoUsd === null || banco?.cupoUsd === undefined ? '' : String(banco.cupoUsd),
  );
  const [activo, setActivo] = useState(banco?.activo ?? true);
  const [aviso, setAviso] = useState<string | null>(null);
  const enviar = async () => {
    setAviso(null);
    try {
      await guardar.mutateAsync({
        id: banco?.id ?? null,
        datos: {
          financiador,
          nombre: nombre.trim(),
          nombreEnPackingList: enPl.trim() || null,
          comisionInicial: dePorcentaje(comision),
          costoDeFondos: dePorcentaje(cof),
          cupoUsd: cupo.trim() === '' ? null : Number(cupo.replace(/[^\d]/g, '')),
          activo,
          ...(banco ? {version: banco.version, notas: banco.notas} : {}),
        },
      });
      if (!banco) setAviso(tr('Agregado.', 'Added.'));
    } catch (e) {
      setAviso(e instanceof Error ? e.message : String(e));
    }
  };
  const etiqueta = banco ? banco.nombre : tr('Banco nuevo', 'New bank');
  return (
    <form
      className="grid gap-2 sm:grid-cols-[2fr_2fr_7rem_7rem_9rem_auto_auto] sm:items-end"
      aria-label={etiqueta}
      onSubmit={e => {
        e.preventDefault();
        if (nombre.trim()) void enviar();
      }}
    >
      <label className="block text-xs text-apagado">
        {banco ? tr('Banco', 'Bank') : tr('Banco nuevo', 'New bank')}
        <NotaDeAyuda texto={A.bancoNombre} dentroDeEtiqueta />
        <input value={nombre} disabled={!puedeEditar} onChange={e => setNombre(e.target.value)} className={campo} />
      </label>
      <label className="block text-xs text-apagado">
        {tr('Nombre en el packing list', 'Name on the packing list')}
        <NotaDeAyuda texto={A.bancoPackingList} dentroDeEtiqueta />
        <input value={enPl} disabled={!puedeEditar} onChange={e => setEnPl(e.target.value)} className={campo} />
      </label>
      <label className="block text-xs text-apagado">
        {tr('Comisión inicial (%)', 'Initial fee (%)')}
        <NotaDeAyuda texto={A.comisionInicial} dentroDeEtiqueta />
        <input
          value={comision}
          disabled={!puedeEditar}
          inputMode="decimal"
          onChange={e => setComision(e.target.value)}
          className={campo}
        />
      </label>
      <label className="block text-xs text-apagado">
        {tr('COF (%)', 'COF (%)')}
        <NotaDeAyuda texto={A.costoDeFondos} dentroDeEtiqueta />
        <input
          value={cof}
          disabled={!puedeEditar}
          inputMode="decimal"
          onChange={e => setCof(e.target.value)}
          className={campo}
        />
      </label>
      <label className="block text-xs text-apagado">
        {tr('Cupo (USD)', 'Credit line (USD)')}
        <NotaDeAyuda texto={A.bancoCupo} dentroDeEtiqueta />
        <input
          value={cupo}
          disabled={!puedeEditar}
          inputMode="numeric"
          onChange={e => setCupo(e.target.value)}
          className={`${campo} text-right tabular-nums`}
        />
      </label>
      <label className="flex items-center gap-1 pb-1.5 text-xs text-apagado">
        <input type="checkbox" checked={activo} disabled={!puedeEditar} onChange={e => setActivo(e.target.checked)} />
        {tr('Activo', 'Active')}
        <NotaDeAyuda texto={A.bancoActivo} dentroDeEtiqueta />
      </label>
      {puedeEditar && (
        <div className="flex items-center gap-2 pb-0.5">
          <button
            type="submit"
            disabled={!nombre.trim() || guardar.isPending}
            className="rounded-md border border-linea bg-white px-2 py-1 text-sm hover:bg-papel disabled:opacity-50"
          >
            {banco ? tr('Guardar', 'Save') : tr('＋ Agregar', '＋ Add')}
          </button>
          {aviso && <span className="text-xs">{aviso}</span>}
        </div>
      )}
    </form>
  );
}
